import { Component, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import {
  SKILL_DESCRIPTION_MAX,
  SKILLS_MAX_PER_BOT,
  Skill,
  SkillFile,
  SkillService,
} from '../../../services/skill.service';
import { TutorService } from '../../../services/tutor.service';

/**
 * One skill: write or review it, its reference files, which agents get it.
 *
 * Route `/skills/new` writes a new one (active right away — it is the
 * operator's own text). An imported skill arrives inactive with its import
 * report on top; switching it on is the moment its text becomes the
 * operator's instruction, so the page says to read it first.
 */
@Component({
  selector: 'app-skill-editor',
  templateUrl: './skill-editor.component.html',
  styleUrl: './skill-editor.component.scss',
})
export class SkillEditorComponent implements OnInit {
  skill: Skill | null = null;
  isNew = true;
  loading = false;
  saving = false;

  name = '';
  title = '';
  description = '';
  body = '';
  isActive = true;

  agents: { id: string; name: string; assigned: boolean }[] = [];

  openFile: Record<string, string | null> = {};
  newFilePath = '';
  newFileContent = '';

  readonly descriptionMax = SKILL_DESCRIPTION_MAX;
  readonly maxPerAgent = SKILLS_MAX_PER_BOT;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private skills: SkillService,
    private tutorService: TutorService,
    private toastr: NbToastrService,
    private t: TranslateService
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('skillId');
    this.isNew = !id || id === 'new';
    if (!this.isNew) this.load(id!);
  }

  get needsReview(): boolean {
    return !!this.skill && this.skill.source === 'import' && !this.skill.is_active;
  }

  private load(id: string): void {
    this.loading = true;
    this.skills.get(id).subscribe({
      next: (s) => {
        this.loading = false;
        this.apply(s);
        this.loadAgents();
      },
      error: () => {
        this.loading = false;
        this.router.navigate(['/agent-setup/skills']);
      },
    });
  }

  private apply(s: Skill): void {
    this.skill = s;
    this.name = s.name;
    this.title = s.title || '';
    this.description = s.description;
    this.body = s.body_md || '';
    this.isActive = s.is_active;
    for (const a of this.agents) a.assigned = s.assigned_bot_ids.includes(a.id);
  }

  private loadAgents(): void {
    this.tutorService.listTutors().subscribe({
      next: (res: any) => {
        const assigned = new Set(this.skill?.assigned_bot_ids || []);
        this.agents = ((res?.data || []) as any[])
          .map((b) => ({ id: String(b.id), name: String(b.name || b.id), assigned: assigned.has(String(b.id)) }))
          .sort((a, b) => a.name.localeCompare(b.name));
      },
    });
  }

  save(form: NgForm): void {
    if (form.invalid) {
      form.form.markAllAsTouched();
      return;
    }
    const input = {
      name: this.name.trim(),
      title: this.title.trim() || null,
      description: this.description.trim(),
      body_md: this.body,
      is_active: this.isActive,
    };
    this.saving = true;
    const req$ = this.isNew ? this.skills.create(input) : this.skills.update(this.skill!.id, input);
    req$.subscribe({
      next: (s) => {
        this.saving = false;
        this.toastr.success(this.t.instant('pages.skills.saved'), '');
        if (this.isNew) {
          this.isNew = false;
          this.router.navigate(['/skills', s.id], { replaceUrl: true });
          this.apply(s);
          this.loadAgents();
        } else {
          this.apply(s);
        }
      },
      error: (err) => this.fail(err),
    });
  }

  toggleAgent(agent: { id: string; assigned: boolean }, assigned: boolean): void {
    if (!this.skill) return;
    const previous = agent.assigned;
    agent.assigned = assigned; // mirror first (CLAUDE.md rule 17)
    const ids = this.agents.filter((a) => a.assigned).map((a) => a.id);
    this.skills.setBots(this.skill.id, ids).subscribe({
      next: (s) => (this.skill = s),
      error: (err) => {
        agent.assigned = previous;
        this.fail(err);
      },
    });
  }

  toggleFile(file: SkillFile): void {
    if (this.openFile[file.id] !== undefined) {
      delete this.openFile[file.id];
      return;
    }
    this.openFile[file.id] = null;
    this.skills.getFile(this.skill!.id, file.id).subscribe({
      next: (f) => (this.openFile[file.id] = f.content),
      error: (err) => {
        delete this.openFile[file.id];
        this.fail(err);
      },
    });
  }

  deleteFile(file: SkillFile): void {
    this.skills.deleteFile(this.skill!.id, file.id).subscribe({
      next: (s) => this.apply(s),
      error: (err) => this.fail(err),
    });
  }

  addFile(): void {
    const path = this.newFilePath.trim();
    if (!path || !this.newFileContent.trim()) {
      this.toastr.warning(this.t.instant('pages.skills.editor.fileMissing'), '');
      return;
    }
    this.skills.putFile(this.skill!.id, path, this.newFileContent).subscribe({
      next: (s) => {
        this.apply(s);
        this.newFilePath = '';
        this.newFileContent = '';
      },
      error: (err) => this.fail(err),
    });
  }

  private fail(err: any): void {
    this.saving = false;
    const detail = err?.error?.detail;
    this.toastr.danger(typeof detail === 'string' ? detail : this.t.instant('pages.skills.errors.saveFailed'), '');
  }

  trackFile = (_: number, f: SkillFile) => f.id;
  trackAgent = (_: number, a: { id: string }) => a.id;
}
